/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import {
  AuxPower,
  BidRequestInputApprovalStatus,
  type BidRequestProcurementInputs,
  bidProducts,
  bidRequestInputTeamApprovals,
  type ConfigurationWithoutRequest,
  coreLandSpecificDataSchema,
  deliveryPoints,
  type ESSCostingConfiguration,
  essAvailabilityGuaranteeStructures,
  type FIMOConfiguration,
  formatDate,
  type ICRequestRecord,
  type IcrsPerProject,
  NetworkUpgradeTiming,
  opportunityTransactions,
  PlantConfiguration,
  type PriceLookup,
  type ProjectListEntry,
  type ProjectListExtendedEntry,
  type PVCostingConfiguration,
  type PVSimConfiguration,
  probabilityOfExceedances,
  type SharepointFile,
} from '@poweralpha/common';
import { isEmpty, uniq, uniqBy } from 'lodash';
import * as z from 'zod';
import { checkPaConfigs } from '../project/schemas';
import {
  percentTransform,
  requiredPercentTransform,
  zodInputStringPipe,
} from '../validation-utils';
import type { NotificationAudiencePerProjectForm } from './models';

const scenarioSchema = z
  .object({
    id: z.number().nullable(),
    index: z.int(),
    products: z.array(z.enum(bidProducts)).min(1, 'Products are required'),
    wtsf: z.string().nonempty('What to Solve For (WTSF) is required'),
    applicable_icrs: z.array(z.string()),
    project_id: z.string(),
    plant_type: z.string().nonempty('Plant type is required'),
    cod: z.date(),
    delivery_point: z.enum(deliveryPoints),
    delivery_point_hub: z.string().optional(),
    poi_mw_ac: zodInputStringPipe(z.number().min(1, 'Must be >=1')),
    contracted_term: zodInputStringPipe(z.number().int().min(0).max(40).optional()),
    pv_ac_capacity: zodInputStringPipe(z.number().min(0).optional()),
    pv_ppa_price: zodInputStringPipe(z.number().min(0).optional()),
    pv_ppa_escalation: zodInputStringPipe(
      z.number().min(0).max(100).optional().transform(percentTransform),
    ),
    ess_capacity: zodInputStringPipe(z.number().min(0).optional()),
    ess_power: zodInputStringPipe(z.number().min(0).optional()),
    ess_term: zodInputStringPipe(z.number().int().min(0).max(40).optional()),
    ess_cycles: zodInputStringPipe(z.number().int().min(0).max(730).optional()),
    ess_maintain_capacity: z.boolean(),
    ess_capacity_payment: zodInputStringPipe(z.number().min(500).max(100e3).optional()),
    ess_capacity_payment_esc: zodInputStringPipe(
      z.number().min(0).max(100).optional().transform(percentTransform),
    ),
    ess_availability_guarantee_structure: z.enum(essAvailabilityGuaranteeStructures),
    ess_availability_guarantee_perc: zodInputStringPipe(
      z.number().min(0).max(100).optional().transform(percentTransform),
    ),
    ess_has_specific_offtake_power_factor: z.boolean(),
    ess_specific_offtake_power_factor: zodInputStringPipe(z.number().min(0.01).max(1).optional()),
    wind_capacity: zodInputStringPipe(z.number().min(0).optional()),
    wind_ppa_price: zodInputStringPipe(z.number().min(0).optional()),
    notes: z.string().optional(),
  })
  .check((ctx) => {
    const { products, plant_type, delivery_point, delivery_point_hub, ess_capacity, ess_power } =
      ctx.value;
    if (
      delivery_point === 'Hub settled' &&
      (!delivery_point_hub || delivery_point_hub.trim() === '')
    ) {
      ctx.issues.push({
        code: 'custom',
        message: 'Hub is required',
        path: ['delivery_point_hub'],
        input: ctx.value.delivery_point_hub,
      });
    }
    if (ess_capacity && !ess_power) {
      ctx.issues.push({
        code: 'custom',
        message: 'ESS power is required',
        path: ['ess_power'],
        input: ess_power,
      });
    }
    if (!products.includes('Asset sale (build-transfer)') && !ctx.value.contracted_term) {
      ctx.issues.push({
        code: 'custom',
        message: 'Contracted term is required',
        path: ['contracted_term'],
        input: ctx.value.contracted_term,
      });
    }
    if (
      plant_type.includes(PlantConfiguration.PV) &&
      plant_type.includes(PlantConfiguration.BESS) &&
      !products.includes('Asset sale (build-transfer)') &&
      !ctx.value.ess_term
    ) {
      ctx.issues.push({
        code: 'custom',
        message: 'ESS term is required if PV+S',
        path: ['ess_term'],
        input: ctx.value.ess_term,
      });
    }
    if (
      plant_type.includes(PlantConfiguration.BESS) &&
      ctx.value.ess_has_specific_offtake_power_factor &&
      !ctx.value.ess_specific_offtake_power_factor
    ) {
      ctx.issues.push({
        code: 'custom',
        message: 'Specific offtake power factor is required',
        path: ['ess_specific_offtake_power_factor'],
        input: ctx.value.ess_specific_offtake_power_factor,
      });
    }
    if (
      plant_type.includes(PlantConfiguration.BESS) &&
      ctx.value.ess_availability_guarantee_structure !== 'not_required' &&
      !ctx.value.ess_availability_guarantee_perc
    ) {
      ctx.issues.push({
        code: 'custom',
        message: 'Availability guarantee is required',
        path: ['ess_availability_guarantee_perc'],
        input: ctx.value.ess_availability_guarantee_perc,
      });
    }
  });

export const scenarioWithProjectSchema = scenarioSchema
  .safeExtend({
    project: z
      .custom<ProjectListExtendedEntry | ProjectListEntry>()
      .nullable()
      .refine((val) => val !== null, 'Project is required'),
    icrs_per_project: z.custom<IcrsPerProject>().optional(),
    edited: z.boolean(),
  })
  .check((ctx) => {
    const { project, icrs_per_project } = ctx.value;
    if (icrs_per_project ? isEmpty(Object.values(icrs_per_project).at(0)) : false) {
      ctx.issues.push({
        code: 'custom',
        message: 'Project does not have associated ICRs',
        path: ['project'],
        input: project,
      });
    }
  });

export type BidRequestScenarioForm = z.input<typeof scenarioWithProjectSchema>;

const icrSchema = z
  .object({
    id: z.string().min(1, 'ICR ID is required'),
    name: z.string().min(1, 'ICR name is required'),
    icr: z
      .custom<ICRequestRecord>()
      .nullable()
      .refine((val) => val !== null, 'ICR is required'),
    aux_power: z.string().min(1, 'Aux power is required'),
    ic_voltage_kv: zodInputStringPipe(z.number().min(0, 'IC Voltage is required')),
    poi_mw_ac: zodInputStringPipe(z.number().min(0, 'POI limit is required')),
    lc_posting_date: z.date({
      error: 'LC posting date is required',
    }),
    lc_posting_cost: zodInputStringPipe(z.number().min(0, 'LC posting cost is required')),
    nu_payment_timing: z.string().min(1, 'Network upgrade timing is required'),
    nu_payment_timing_other_comments: z.string().optional(),
    reimb_nu_costs: zodInputStringPipe(
      z.number().min(0, 'Reimb. network upgrade cost is required'),
    ),
    nonreimb_nu_costs: zodInputStringPipe(
      z.number().min(0, 'Nonreimb. network upgrade cost is required'),
    ),
    refund_interest_rate: zodInputStringPipe(
      z.number().min(0).optional().transform(percentTransform),
    ),
    refund_period_months: zodInputStringPipe(z.number().min(0).optional()),
    aux_power_comment: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.icr == null) {
      ctx.addIssue({
        code: 'custom',
        message: 'IC request is required',
        path: ['icr'],
        input: val.icr,
      });
    }

    if (
      val.nu_payment_timing === NetworkUpgradeTiming.OTHER &&
      (!val.nu_payment_timing_other_comments || val.nu_payment_timing_other_comments.trim() === '')
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Network upgrade timing comment is required',
        path: ['nu_payment_timing_other_comments'],
        input: val.nu_payment_timing_other_comments,
      });
    }

    if (typeof val.reimb_nu_costs === 'number' && val.reimb_nu_costs > 0) {
      if (val.refund_interest_rate === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'Refund interest rate is required',
          path: ['refund_interest_rate'],
          input: val.refund_interest_rate,
        });
      }
      if (val.refund_period_months === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'Refund period is required',
          path: ['refund_period_months'],
          input: val.refund_period_months,
        });
      }
    }

    if (val.aux_power === AuxPower.TARIFF_STRUCTURE) {
      if (!val.aux_power_comment || val.aux_power_comment.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'Aux power comment is required',
          path: ['aux_power_comment'],
          input: val.aux_power_comment,
        });
      }
    }
  });

export type IcrForm = z.input<typeof icrSchema>;
export type IcrFormParsed = z.infer<typeof icrSchema>;

export const coreLandSchema = z.object({
  lease_cost: zodInputStringPipe(z.number().min(0, 'Land lease cost is required')),
  purchase_cost: zodInputStringPipe(z.number().min(0, 'Land purchase cost is required')),
  purchase_acres_under_control: zodInputStringPipe(
    z.number().min(0, 'Purchase acres under control is required'),
  ),
  lease_acres_under_control: zodInputStringPipe(
    z.number().min(0, 'Lease acres under control is required'),
  ),
  lease_escalation: zodInputStringPipe(
    z
      .number()
      .min(0, 'Land lease escalation is required')
      .max(20, '<=20%')
      .transform(requiredPercentTransform),
  ),
  owned_in_fee_acres: zodInputStringPipe(z.number().min(0, 'Owned in fee acres is required')),
  owned_in_fee_cost: zodInputStringPipe(z.number().min(0, 'Owned in fee cost is required')),
});

const selectedSitesSchema = z.object({
  custom: coreLandSchema.nullable(),
  lease: z.array(z.string()),
  purchase: z.array(z.string()),
  owned_in_fee: z.array(z.string()),
});

export type SupplementaryFilesForm = z.input<typeof supplementaryFilesSchema>;

export const supplementaryFilesSchema = z.object({
  supplementary_xlsx_files: z.array(z.file()),
  previously_uploaded_xlsx_files: z.array(z.custom<SharepointFile>()),
});

export const editDevBidRequestSchema = z.object({
  land: z
    .object({
      ...coreLandSchema.shape,
      coordinates_lat: zodInputStringPipe(z.number().min(-90).max(90)),
      coordinates_long: zodInputStringPipe(z.number().min(-180).max(180)),
      miles_to_ic: zodInputStringPipe(z.number().min(0, 'Miles to IC is required')),
      min_purchase_acres: zodInputStringPipe(z.number()),
      min_lease_acres: zodInputStringPipe(z.number()),
      total_easement_costs: zodInputStringPipe(
        z.number().min(0, 'Gen-tie land costs during construction is required'),
      ),
      site_perc_timber: zodInputStringPipe(
        z
          .number()
          .min(0, 'Site perc timber is required')
          .max(100, '<=100%')
          .transform(requiredPercentTransform),
      ),
      land_comment: z.string().nullable().optional(),
      selected_sites: z.object({
        default: z.object({
          ...selectedSitesSchema.shape,
          custom: z.null().optional(),
        }),
        per_scenario: z.record(
          z.string(),
          z.object({
            ...selectedSitesSchema.shape,
            custom: coreLandSpecificDataSchema.nullable().optional(),
          }),
        ),
      }),
    })
    .superRefine((val, ctx) => {
      if (val.min_purchase_acres > val.purchase_acres_under_control) {
        ctx.addIssue({
          code: 'custom',
          message: 'Must be <= than purchase acres',
          path: ['min_purchase_acres'],
          input: val.min_purchase_acres,
        });
      }
      if (val.min_lease_acres > val.lease_acres_under_control) {
        ctx.addIssue({
          code: 'custom',
          message: 'Must be <= than lease acres',
          path: ['min_lease_acres'],
          input: val.min_lease_acres,
        });
      }
    }),

  budget: z
    .object({
      is_devex_por: z.boolean(),
      devex_budget: zodInputStringPipe(z.number().min(1, 'Must be non-zero')),
      energy_communities_bonus: z.boolean(),
      budget_comment: z.string().optional(),
      discretionary_costs: z.array(
        z.object({
          name: z.string().min(3, 'Name is required'),
          date: z.date({ error: 'Date is required' }),
          value: zodInputStringPipe(
            z.number().refine((v) => v !== 0, { message: 'Must be nonzero' }),
          ),
        }),
      ),
    })
    .superRefine((val, ctx) => {
      if (!val.is_devex_por) {
        if (!val.budget_comment || val.budget_comment.trim().length < 5) {
          ctx.addIssue({
            code: 'custom',
            message: 'Budget comment is required if DevEx budget is not POR',
            path: ['budget_comment'],
            input: val.budget_comment,
          });
        }
      }
      if (uniqBy(val.discretionary_costs, 'name').length !== val.discretionary_costs.length) {
        ctx.addIssue({
          code: 'custom',
          message: 'Costs must be unique',
          path: ['discretionary_costs'],
          input: val.discretionary_costs,
        });
      }
    }),
  files: supplementaryFilesSchema,
  bid_matrix: z.array(scenarioWithProjectSchema),
});

export const bidMatrixSchema = z.object({
  bid_matrix: z.array(scenarioWithProjectSchema).refine((val) => {
    const hashed = val.map((scenario) =>
      Object.entries(scenario)
        .filter(([key]) => key !== 'id')
        .map(([, v]) => v)
        .join(''),
    );
    return uniq(hashed).length === hashed.length;
  }, 'Bid matrix scenarios must be unique'),
  plant_types: z.array(z.string()).min(1, 'Plant types are required'),
  deleted_scenarios: z.array(z.number()),
});

export type BidMatrixFormParsed = z.infer<typeof bidMatrixSchema>;
export type EditBidMatrixForm2 = z.input<typeof bidMatrixSchema>;

export const editOrigBidRequestSchema = z.object({
  comment: z.string().min(10, 'Min 10 characters').max(1000, 'Max 1000 characters'),
  opportunity_data: z
    .object({
      transaction: z.enum(opportunityTransactions),
      ppa_security_pre_cod: zodInputStringPipe(z.number().nonnegative().optional()),
      ppa_security_post_cod: zodInputStringPipe(z.number().nonnegative().optional()),
      ppa_security_pre_cod_start_date: z.date().nullable(),
      offtake_bid_fee: zodInputStringPipe(z.number().nonnegative().optional()),
      offtake_success_broker_fee: zodInputStringPipe(z.number().nonnegative().optional()),
    })
    .check((ctx) => {
      const { transaction } = ctx.value;
      if (transaction === 'PPA') {
        if (ctx.value.ppa_security_pre_cod === undefined) {
          ctx.issues.push({
            code: 'custom',
            message: 'PPA security pre-COD is required',
            path: ['ppa_security_pre_cod'],
            input: ctx.value.ppa_security_pre_cod,
          });
        }
        if (ctx.value.ppa_security_post_cod === undefined) {
          ctx.issues.push({
            code: 'custom',
            message: 'PPA security post-COD is required',
            path: ['ppa_security_post_cod'],
            input: ctx.value.ppa_security_post_cod,
          });
        }
        if (ctx.value.ppa_security_pre_cod_start_date === undefined) {
          ctx.issues.push({
            code: 'custom',
            message: 'PPA security pre-COD start date is required',
            path: ['ppa_security_pre_cod_start_date'],
            input: ctx.value.ppa_security_pre_cod_start_date,
          });
        }
      }
    }),
  probability_of_exceedance: z.enum(probabilityOfExceedances).optional(),
});

const notifAudiencePerProjectSchema = z.object({
  notifs: z.custom<NotificationAudiencePerProjectForm['notifs']>(),
  others: z.custom<NotificationAudiencePerProjectForm['others']>(),
});

export const newBidRequestSchema = z.object({
  ...editOrigBidRequestSchema.shape,
  ...notifAudiencePerProjectSchema.shape,
  version: z.number().int().nonnegative(),
  name: z
    .string()
    .max(80, 'Name must be less than 80 characters')
    .refine((val) => !val.includes('%'), 'Name must be less than 80 characters'),
  stage: z.string(),
});

export type NewBidRequestFormParsed = z.infer<typeof newBidRequestSchema>;
export type NewBidRequestForm = z.input<typeof newBidRequestSchema>;

export type EditOrigFormParsed = z.infer<typeof editOrigBidRequestSchema>;
export type EditOrigBidRequestForm = z.input<typeof editOrigBidRequestSchema>;

export type EditTxmForm = z.input<typeof editTxmBidRequestSchema>;
export type EditTxmFormParsed = z.infer<typeof editTxmBidRequestSchema>;

export type CoreLandFields = z.input<typeof coreLandSchema>;
export type CoreLandFieldsParsed = z.infer<typeof coreLandSchema>;
export type EditDevForm = z.input<typeof editDevBidRequestSchema>;
export type EditDevFormParsed = z.infer<typeof editDevBidRequestSchema>;

export type NotifAudiencePerProjectFormParsed = z.infer<typeof notifAudiencePerProjectSchema>;

export const editTxmBidRequestSchema = z
  .object({
    project_id: z
      .string()
      .nullable()
      .refine((val) => val !== null, 'Project ID is required'),
    bid_matrix: z.array(scenarioWithProjectSchema),
    icr_data: z.array(icrSchema).min(1, 'Min 1 ICR required'),
    project_txm_data: z.object({
      icr_ids: z.array(z.string()).min(1, 'Min 1 ICR required'),
      merchant: z.object({
        prices: z
          .custom<PriceLookup>()
          .nullable()
          .refine((val) => val !== null, 'Merchant prices are required'),
      }),
      curtailment: zodInputStringPipe(z.number().min(0).optional()).transform(percentTransform),
      contract_period_wheeling_first_year: zodInputStringPipe(z.number().min(0).optional()),
      merchant_period_wheeling_first_year: zodInputStringPipe(z.number().min(0).optional()),
      txm_comment: z.string().optional(),
    }),
    files: supplementaryFilesSchema,
  })
  .superRefine((val, ctx) => {
    val.bid_matrix.forEach((scenario) => {
      if (scenario.applicable_icrs.length === 0 && scenario.project_id === val.project_id) {
        ctx.addIssue({
          code: 'custom',
          message: 'At least one ICR must be selected for each scenario',
          path: ['project_txm_data'],
        });
      }
    });
  });

export type AddConfigsForm = z.input<typeof addConfigsSchema>;
export type AddConfigsFormParsed = z.infer<typeof addConfigsSchema>;
export type AddConfigScenario = AddConfigsForm['scenarios'][number];

export const getConfigEntrySchema = <T>() =>
  z.object({
    config: z.custom<ConfigurationWithoutRequest>().nullable().default(null),
    request: z.custom<T>().nullable().default(null),
    version_fixed: z.boolean().default(false),
  });

const genericConfigEntryFormSchema = getConfigEntrySchema<unknown>();
export type ConfigEntryForm = z.input<typeof genericConfigEntryFormSchema>;
export type ConfigEntryFormParsed = z.infer<typeof genericConfigEntryFormSchema>;

const formatCodForComparison = (cod: Date | string | null | undefined) =>
  cod ? formatDate(cod, 'YYYY-MM-DD') : undefined;

const codMatchesScenario = (scenarioCod: Date, configCod: string | null | undefined) => {
  const scenarioCodValue = formatCodForComparison(scenarioCod);
  const configCodValue = formatCodForComparison(configCod);
  return !scenarioCodValue || !configCodValue || scenarioCodValue === configCodValue;
};

const isEssOnlyScenario = (plantType: string) =>
  plantType.includes(PlantConfiguration.BESS) && !plantType.includes(PlantConfiguration.PV);

const isPvPlusStorageEssConfig = (request: ESSCostingConfiguration['request'] | null | undefined) =>
  (request?.project?.pv_size ?? 0) > 0;

export const addConfigEntrySchema = z
  .object({
    ...scenarioSchema.shape,
    bid_matrix_id: z.number(),
    pa_scenario_id: z.uuid().optional().nullable(),
    pv: getConfigEntrySchema<PVCostingConfiguration['request']>(),
    pvsim: getConfigEntrySchema<PVSimConfiguration['request']>(),
    ess: getConfigEntrySchema<ESSCostingConfiguration['request']>(),
    fimo: getConfigEntrySchema<FIMOConfiguration['request']>(),
    procurement_inputs: z.custom<BidRequestProcurementInputs>().nullable().default(null),
    comments: z.string().optional().nullable(),
  })
  .check((ctx) => {
    const { cod, plant_type, pv, ess, fimo, pvsim } = ctx.value;
    if (plant_type.includes(PlantConfiguration.PV) && !pv.config) {
      ctx.issues.push({
        code: 'custom',
        message: 'PV configuration is required',
        path: ['pv.config'],
        input: pv.config,
      });
    }

    if (plant_type.includes(PlantConfiguration.BESS) && !ess.config) {
      ctx.issues.push({
        code: 'custom',
        message: 'BESS configuration is required',
        path: ['ess.config'],
        input: ess.config,
      });
    }

    if (
      plant_type.includes(PlantConfiguration.PV) &&
      pv.config &&
      !codMatchesScenario(cod, pv.request?.cod)
    ) {
      ctx.issues.push({
        code: 'custom',
        message: 'PV config COD must match bid request scenario COD',
        path: ['pv.config'],
        input: pv.config,
      });
    }

    if (
      plant_type.includes(PlantConfiguration.BESS) &&
      ess.config &&
      !codMatchesScenario(cod, ess.request?.project?.cod)
    ) {
      ctx.issues.push({
        code: 'custom',
        message: 'ESS config COD must match bid request scenario COD',
        path: ['ess.config'],
        input: ess.config,
      });
    }

    if (fimo.config && !codMatchesScenario(cod, fimo.request?.base?.cod)) {
      ctx.issues.push({
        code: 'custom',
        message: 'FIMO config COD must match bid request scenario COD',
        path: ['fimo.config'],
        input: fimo.config,
      });
    }

    if (ess.config && isEssOnlyScenario(plant_type) && isPvPlusStorageEssConfig(ess.request)) {
      const message = 'ESS config is PV+S but bid request scenario is ESS-only';
      ctx.issues.push(
        {
          code: 'custom',
          message,
          path: ['ess.request'],
          input: ess.request,
        },
        {
          code: 'custom',
          message,
          path: ['ess.config'],
          input: ess.config,
        },
      );
    }

    checkPaConfigs(
      pv,
      pvsim,
      ess,
      fimo,
      plant_type.split('+') as Array<PlantConfiguration>,
      ctx.issues,
    );
  });

export const addConfigsSchema = z.object({
  scenarios: z.array(addConfigEntrySchema),
});

export const reviewBrSchema = z
  .object({
    notifications: notifAudiencePerProjectSchema,
    status: z.enum(BidRequestInputApprovalStatus),
    approvals: z.array(z.enum(bidRequestInputTeamApprovals)).min(1),
    peLevel: z.enum(probabilityOfExceedances),
    comments: z.string().optional().nullable(),
  })
  .check((ctx) => {
    const { comments, status, peLevel } = ctx.value;
    if (
      !comments &&
      (status === BidRequestInputApprovalStatus.CHANGES_REQUESTED || peLevel !== 'P50')
    ) {
      ctx.issues.push({
        code: 'custom',
        message: 'Comments are required',
        path: ['comments'],
        input: comments,
      });
    }
  });

export type ReviewBidRequestForm = z.infer<typeof reviewBrSchema>;
